/**
 * MatchHubSearchInput — shared debounced search dropdown for MatchHub queries.
 *
 * The four round-2 surfaces (match wizard, team-link drawer, bulk-link modal,
 * face-tag PersonModal) all need the same pattern: ≥2 chars + 250ms debounce →
 * search call → results dropdown → onSelect. They differ only in the row
 * template and which `searchFn` they call.
 *
 * Props
 * -----
 *   searchFn(query) → Promise<{...} | rows>     — required. Should return a list
 *                                                  of result objects, or an object
 *                                                  whose `dataKey` field is one.
 *   dataKey                                      — optional. When the searchFn
 *                                                  resolves to an object (the
 *                                                  matchhub gateway payload
 *                                                  shape, e.g. `{matches: [...]}`),
 *                                                  pull rows from this key. When
 *                                                  unset, the resolved value is
 *                                                  treated as the row array.
 *   renderRow(item)                              — required. JSX for one row.
 *   onSelect(item)                               — required. Called with the
 *                                                  full row object on click.
 *   placeholder                                  — optional. Default: 'Search…'.
 *   disabled                                     — optional. Disables both input
 *                                                  and dropdown.
 *   minLength                                    — optional. Default 2.
 *   debounceMs                                   — optional. Default 250.
 *   initialQuery                                 — optional. Pre-fill the input
 *                                                  (e.g. when starting a manual
 *                                                  per-row search with the local
 *                                                  Person's name as a seed).
 *
 * The wrapping component owns selection state — MatchHubSearchInput just
 * orchestrates query → debounce → search → render → bubble.
 */
import { useEffect, useRef, useState } from 'react';

const DEFAULT_DEBOUNCE_MS = 250;
const DEFAULT_MIN_LENGTH = 2;

export default function MatchHubSearchInput({
  searchFn,
  dataKey,
  renderRow,
  onSelect,
  placeholder = 'Search…',
  disabled = false,
  minLength = DEFAULT_MIN_LENGTH,
  debounceMs = DEFAULT_DEBOUNCE_MS,
  initialQuery = '',
  className,
  rowClassName,
  listClassName,
  inputClassName,
}) {
  const [query, setQuery] = useState(initialQuery);
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const timerRef = useRef(null);
  // Latest-wins guard — a stale resolve from a quick keystroke must not
  // clobber the current query's results.
  const seqRef = useRef(0);

  useEffect(() => {
    if (timerRef.current) clearTimeout(timerRef.current);
    if (disabled) {
      setResults([]);
      setLoading(false);
      return undefined;
    }
    if (!query || query.length < minLength) {
      setResults([]);
      setLoading(false);
      setError(null);
      return undefined;
    }
    setLoading(true);
    setError(null);
    const mySeq = ++seqRef.current;
    timerRef.current = setTimeout(async () => {
      try {
        const data = await searchFn(query);
        if (mySeq !== seqRef.current) return; // a newer query already started
        const rows = dataKey
          ? (data?.[dataKey] || [])
          : (Array.isArray(data) ? data : data?.results || []);
        setResults(rows);
      } catch (err) {
        if (mySeq !== seqRef.current) return;
        setError(err?.message || 'Search failed');
        setResults([]);
      } finally {
        if (mySeq === seqRef.current) setLoading(false);
      }
    }, debounceMs);
    return () => clearTimeout(timerRef.current);
  }, [query, disabled, searchFn, dataKey, minLength, debounceMs]);

  return (
    <div className={className}>
      <input
        type="text"
        className={inputClassName}
        value={query}
        onChange={(e) => setQuery(e.target.value)}
        placeholder={placeholder}
        disabled={disabled}
        autoComplete="off"
      />
      {loading && <div style={{ fontSize: 12, opacity: 0.6, padding: '4px 0' }}>Searching…</div>}
      {error && (
        <div style={{ fontSize: 12, color: 'var(--danger, #ff6b6b)', padding: '4px 0' }}>
          {error}
        </div>
      )}
      {!loading && results.length > 0 && (
        <ul className={listClassName} style={{ listStyle: 'none', margin: 0, padding: 0 }}>
          {results.map((item, idx) => (
            <li
              key={item.id ?? item.provider_id ?? idx}
              className={rowClassName}
              onClick={() => {
                onSelect(item);
                setQuery('');
                setResults([]);
              }}
              role="button"
              tabIndex={0}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  onSelect(item);
                  setQuery('');
                  setResults([]);
                }
              }}
              style={{ cursor: 'pointer' }}
            >
              {renderRow(item)}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
